import { motion } from "framer-motion";
import { ShieldCheck, TrendingUp, FileWarning, ArrowRight } from "lucide-react";
import { IMAGES } from "@/data/content";
import { Overline, CTAButton } from "@/components/ui-bits";
import { Reveal, staggerParent, staggerChild } from "@/components/motion-primitives";

const HIGHLIGHTS = [
  { icon: FileWarning, title: "Paying isn't the same as correct", text: "Estimated readings, tariff changes and adjustments can affect your account without being noticed." },
  { icon: TrendingUp, title: "Consumption trend monitoring", text: "We watch for abnormal usage and estimated billing across your statements each month." },
  { icon: ShieldCheck, title: "Early irregularity detection", text: "Ongoing monitoring helps identify potential irregularities as early as possible." },
];

export const AccountManagement = () => (
  <section id="account-management" className="relative py-24 md:py-32 bg-muted/40 overflow-hidden">
    <div className="container mx-auto px-4 md:px-8">
      <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
        <Reveal className="order-2 lg:order-1">
          <div className="relative">
            <div className="rounded-2xl overflow-hidden shadow-[0_30px_80px_-30px_rgba(15,23,42,0.35)]">
              <img src={IMAGES.residential} alt="Aerial view of a residential estate under proactive municipal account management" className="w-full h-[380px] md:h-[500px] object-cover" loading="lazy" />
            </div>
            <div className="absolute -top-5 -left-3 md:left-6 bg-secondary text-white rounded-xl px-5 py-4 shadow-lg">
              <div className="font-heading text-2xl font-semibold leading-none">Monthly</div>
              <div className="text-xs uppercase tracking-wider mt-1 text-white/85">Proactive monitoring</div>
            </div>
          </div>
        </Reveal>

        <div className="order-1 lg:order-2">
          <Reveal><Overline>Account Management</Overline></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-5 text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight text-primary leading-[1.1]">
              Proactive monthly municipal account management
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-base md:text-lg text-muted-foreground leading-relaxed">
              Simply paying your municipal statement each month does not necessarily mean the account is correct.
              SML provides ongoing monitoring to help catch potential issues before they compound.
            </p>
          </Reveal>

          <motion.div variants={staggerParent} initial="hidden" whileInView="show" viewport={{ once: true }} className="mt-8 space-y-4">
            {HIGHLIGHTS.map((h, i) => {
              const Icon = h.icon;
              return (
                <motion.div key={i} variants={staggerChild} className="flex gap-4">
                  <span className="flex items-center justify-center w-11 h-11 rounded-xl bg-white border border-slate-200 text-secondary shrink-0">
                    <Icon className="w-5 h-5" />
                  </span>
                  <div>
                    <h3 className="font-heading font-semibold text-primary">{h.title}</h3>
                    <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{h.text}</p>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>

          <Reveal delay={0.15}>
            <div className="mt-9">
              <CTAButton href="#pricing" variant="primary" icon={ArrowRight} testId="account-mgmt-cta">View Account Management Packages</CTAButton>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);
